import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTabManager } from '../../app-v2/context/tab-manager-context';
import { useEnsembleModelsQuery } from '../hooks/tags';

import { Ensemble } from '@controlrooms/models';

interface ContextProps {
  selectedEnsemble: Ensemble | null;
  setSelectedEnsemble: Dispatch<SetStateAction<Ensemble | null>>;
  ensembleFamilies: Ensemble[];
  showEnsembleErrorBanner: boolean;
  setShowEnsembleErrorBanner: Dispatch<SetStateAction<boolean>>;
}

const defaultState: ContextProps = {
  selectedEnsemble: null,
  setSelectedEnsemble: () => null,
  ensembleFamilies: [],
  showEnsembleErrorBanner: false,
  setShowEnsembleErrorBanner: () => null,
};

export const EnsembleContext = createContext<ContextProps>(defaultState);

const EnsembleContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showEnsembleErrorBanner, setShowEnsembleErrorBanner] = useState<boolean>(false);

  const { activeViewState, updateActiveTabViewState } = useTabManager();
  const { ensembleFamilies = [] } = useEnsembleModelsQuery();

  const selectedEnsemble = activeViewState?.selectedEnsemble ?? null;

  const setSelectedEnsemble = useCallback<Dispatch<SetStateAction<Ensemble | null>>>(
    (value) => {
      if (!activeViewState || !updateActiveTabViewState) return;
      const next =
        typeof value === 'function'
          ? (value as (prev: Ensemble | null) => Ensemble | null)(selectedEnsemble)
          : value;
      updateActiveTabViewState({ ...activeViewState, selectedEnsemble: next });
    },
    [activeViewState, updateActiveTabViewState, selectedEnsemble],
  );

  // Set first item (or main_family) as default selectedEnsemble when tab has none
  useEffect(() => {
    if (
      !activeViewState ||
      !updateActiveTabViewState ||
      selectedEnsemble != null ||
      !ensembleFamilies?.length
    )
      return;
    const defaultEnsemble =
      ensembleFamilies.find(
        (ef: Ensemble) => (ef as Ensemble & { main_family?: boolean }).main_family,
      ) || ensembleFamilies[0];
    if (defaultEnsemble) {
      updateActiveTabViewState({ ...activeViewState, selectedEnsemble: defaultEnsemble });
    }
  }, [activeViewState, updateActiveTabViewState, selectedEnsemble, ensembleFamilies]);

  const appState = useMemo(
    () => ({
      selectedEnsemble,
      setSelectedEnsemble,
      ensembleFamilies: ensembleFamilies ?? [],
      showEnsembleErrorBanner,
      setShowEnsembleErrorBanner,
    }),
    [selectedEnsemble, setSelectedEnsemble, ensembleFamilies, showEnsembleErrorBanner],
  );

  return <EnsembleContext.Provider value={appState}>{children}</EnsembleContext.Provider>;
};

export default EnsembleContextProvider;
